import React, { useState, useEffect, useCallback } from 'react';
import { LayoutGrid, List, Clock, Download, AlertCircle, Loader2, Paperclip, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/utils/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { DocumentStatsComponent } from './DocumentStats';
import { DocumentFilters } from './DocumentFilters';
import { GroupedDocumentsView } from './GroupedDocumentsView';
import { FlatDocumentsView } from './FlatDocumentsView';
import { TimelineDocumentsView } from './TimelineDocumentsView';
import { aggregatedDocumentsService } from '../../services/aggregated-documents-service';
import { clientService } from '../../services/client-service';
import { caseService } from '../../services/case-service';
import { ClientDocumentUploadDialog } from './ClientDocumentUploadDialog';
import type {
  AggregatedDocumentsResponse,
  DocumentFiltersState,
  ViewMode,
  AggregatedClientDocument,
  SavedFilter
} from '../../types/aggregated-documents';

interface ClientDocumentsAggregatedProps {
  clientId: string;
  clientName: string;
  /** Dossiê: sem o quadro de estatísticas. */
  showStats?: boolean;
}

const initialFilters: DocumentFiltersState = {
  sourceTypes: [],
  documentTypes: [],
  search: '',
  onlyConfidential: false,
  startDate: null,
  endDate: null,
  sortBy: 'date',
  sortOrder: 'desc'
};

export const ClientDocumentsAggregated: React.FC<ClientDocumentsAggregatedProps> = ({
  clientId,
  clientName,
  showStats = true
}) => {
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [data, setData] = useState<AggregatedDocumentsResponse | null>(null);
  const [filters, setFilters] = useState<DocumentFiltersState>(initialFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  // Load documents
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: 50,
        sourceTypes: filters.sourceTypes.length > 0 ? filters.sourceTypes : undefined,
        documentTypes: filters.documentTypes.length > 0 ? filters.documentTypes : undefined,
        search: filters.search || undefined,
        onlyConfidential: filters.onlyConfidential || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        groupBy: viewMode === 'grouped' ? ('source' as const) : undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await aggregatedDocumentsService.getAggregatedDocuments(
        clientId,
        params
      );

      setData(response);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Erro ao carregar documentos. Tente novamente.');
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  }, [clientId, filters, page, viewMode]);

  // Load on mount and when filters/page change
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Load saved filters from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`saved-filters-${clientId}`);
    if (saved) {
      try {
        setSavedFilters(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing saved filters:', e);
      }
    }
  }, [clientId]);

  // Handlers
  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  const handleToggleSelect = (docId: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const handleDownload = async (doc: AggregatedClientDocument) => {
    try {
      toast.loading('Baixando documento...', { id: 'download' });

      const blob = await aggregatedDocumentsService.downloadDocument(doc);
      aggregatedDocumentsService.downloadBlob(blob, doc.fileName);

      toast.success('Download concluído', { id: 'download' });
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Erro ao baixar documento', { id: 'download' });
    }
  };

  const handlePreview = async (doc: AggregatedClientDocument) => {
    try {
      toast.loading('Carregando visualização...', { id: 'preview' });

      const blob = await aggregatedDocumentsService.previewDocument(doc);
      aggregatedDocumentsService.previewBlob(blob);

      toast.success('Visualização aberta', { id: 'preview' });
    } catch (err) {
      console.error('Error previewing document:', err);
      toast.error('Erro ao visualizar documento', { id: 'preview' });
    }
  };

  // Descrição editável onde a API aceita (documentos do cliente e dos casos).
  const getDescriptionSaver = (doc: AggregatedClientDocument) => {
    const save =
      doc.sourceType === 'CLIENT' ? (description: string | null) => clientService.updateDocumentDescription(doc.clientId || clientId, doc.id, description)
      : doc.sourceType === 'CASE' ? (description: string | null) => caseService.updateDocumentDescription(doc.sourceEntityId, doc.id, description)
      : undefined;
    if (!save) return undefined;
    return async (description: string | null) => {
      await save(description);
      await loadDocuments();
    };
  };

  const handleBulkDownload = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Selecione pelo menos um documento');
      return;
    }

    try {
      toast.loading('Preparando download...', { id: 'bulk-download' });

      const blob = await aggregatedDocumentsService.downloadMultipleDocuments(
        clientId,
        Array.from(selectedDocs)
      );

      aggregatedDocumentsService.downloadBlob(
        blob,
        `documentos-${clientName.replace(/\s+/g, '-')}-${Date.now()}.zip`
      );

      toast.success(`${selectedDocs.size} documentos baixados com sucesso`, {
        id: 'bulk-download'
      });

      setSelectedDocs(new Set());
    } catch (err) {
      console.error('Error bulk downloading:', err);
      toast.error('Erro ao baixar documentos', { id: 'bulk-download' });
    }
  };

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error('Digite um nome para o filtro');
      return;
    }

    const newFilter: SavedFilter = {
      id: Date.now().toString(),
      name: filterName,
      filters: { ...filters },
      createdAt: new Date()
    };

    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem(`saved-filters-${clientId}`, JSON.stringify(updated));

    toast.success('Filtro salvo com sucesso');
    setShowSaveFilterModal(false);
    setFilterName('');
  };

  const handleLoadFilter = (filter: SavedFilter) => {
    setFilters(filter.filters as DocumentFiltersState);
    toast.success(`Filtro "${filter.name}" aplicado`);
  };

  const handleDeleteFilter = (filterId: string) => {
    const updated = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updated);
    localStorage.setItem(`saved-filters-${clientId}`, JSON.stringify(updated));
    toast.success('Filtro removido');
  };

  const viewModes: { mode: ViewMode; label: string; icon: typeof Clock }[] = [
    { mode: 'grouped', label: 'Visualização agrupada', icon: LayoutGrid },
    { mode: 'flat', label: 'Visualização em lista', icon: List },
    { mode: 'timeline', label: 'Visualização em linha do tempo', icon: Clock }
  ];

  const closeSaveFilter = () => {
    setShowSaveFilterModal(false);
    setFilterName('');
  };

  // Render
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center gap-3 py-12" aria-busy="true">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">Carregando documentos...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden="true" />
          <div>
            <h3 className="font-medium text-destructive">Erro ao carregar documentos</h3>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={loadDocuments}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {showStats && <DocumentStatsComponent stats={data.stats} />}

      <DocumentFilters
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
      />

      {savedFilters.length > 0 && (
        <Card size="sm">
          <CardContent>
            <h4 className="mb-3 text-sm font-semibold">Filtros Salvos</h4>
            <div className="flex flex-wrap gap-2">
              {savedFilters.map((filter) => (
                <div key={filter.id} className="inline-flex items-center overflow-hidden rounded-md border bg-secondary">
                  <button
                    type="button"
                    onClick={() => handleLoadFilter(filter)}
                    className="px-3 py-1 text-sm text-secondary-foreground hover:bg-muted"
                  >
                    {filter.name}
                  </button>
                  <button
                    type="button"
                    aria-label={`Remover filtro ${filter.name}`}
                    onClick={() => handleDeleteFilter(filter.id)}
                    className="border-l px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card size="sm">
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border" role="group" aria-label="Modo de visualização">
              {viewModes.map(({ mode, label, icon: Icon }, index) => (
                <Tooltip key={mode}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setViewMode(mode)}
                      aria-label={label}
                      aria-pressed={viewMode === mode}
                      className={cn(
                        'px-3 py-2 text-sm font-medium transition-colors',
                        index > 0 && 'border-l',
                        viewMode === mode ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              ))}
            </div>

            <span className="text-sm text-muted-foreground">
              {data.total} {data.total === 1 ? 'documento' : 'documentos'}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {selectedDocs.size > 0 && (
              <>
                <span className="text-sm text-muted-foreground">
                  {selectedDocs.size} selecionado{selectedDocs.size !== 1 ? 's' : ''}
                </span>
                <Button onClick={handleBulkDownload}>
                  <Download className="size-4" />
                  Baixar Selecionados
                </Button>
              </>
            )}
            <Button onClick={() => setShowUpload(true)}>
              <Paperclip className="size-4" />
              Anexar documento
            </Button>
            <Button variant="outline" onClick={() => setShowSaveFilterModal(true)}>
              <Save className="size-4" />
              Salvar Filtro
            </Button>
          </div>
        </CardContent>
      </Card>

      {viewMode === 'grouped' && data.groupedBy && (
        <GroupedDocumentsView
          groupedDocs={data.groupedBy}
          selectedDocs={selectedDocs}
          onToggleSelect={handleToggleSelect}
          onDownload={handleDownload}
          onPreview={handlePreview}
          getDescriptionSaver={getDescriptionSaver}
        />
      )}

      {viewMode === 'flat' && (
        <FlatDocumentsView
          documents={data.documents}
          selectedDocs={selectedDocs}
          onToggleSelect={handleToggleSelect}
          onDownload={handleDownload}
          onPreview={handlePreview}
          getDescriptionSaver={getDescriptionSaver}
        />
      )}

      {viewMode === 'timeline' && (
        <TimelineDocumentsView
          documents={data.documents}
          selectedDocs={selectedDocs}
          onToggleSelect={handleToggleSelect}
          onDownload={handleDownload}
          onPreview={handlePreview}
          getDescriptionSaver={getDescriptionSaver}
        />
      )}

      {data.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">Página {page} de {data.totalPages}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
            disabled={page === data.totalPages}
          >
            Próxima
          </Button>
        </div>
      )}

      <ClientDocumentUploadDialog
        clientId={clientId}
        open={showUpload}
        onOpenChange={setShowUpload}
        onUploaded={loadDocuments}
      />

      <Dialog open={showSaveFilterModal} onOpenChange={(open) => { if (!open) closeSaveFilter(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvar Filtro</DialogTitle>
            <DialogDescription>O filtro fica salvo neste navegador para este contato.</DialogDescription>
          </DialogHeader>
          <Input
            type="text"
            aria-label="Nome do filtro"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="Nome do filtro..."
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeSaveFilter}>Cancelar</Button>
            <Button onClick={handleSaveFilter}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
